import { cloneDeep, isEmpty } from 'lodash';
import { ArticleType, IVehicleStockEcomItem, TaxType, VatCode } from 'sales-types-common';
import {
  EnumMapper,
  IConfiguration,
  INextArticle,
  IOrder,
  isConfirmedOrder,
  mapVehicleConditionForForeignBrand,
  OrderEventType,
} from 'next-common';
import { IVehicleChargeStatus } from '../../common';
import { calculateVat } from './vat';
import { isCancelOrder, isCompletedOrder, isOffer, VehicleConfig } from './vehicleOrder';
import { VehicleCondition } from 'sales-types-common';
import { findChargeTypeValue } from 'sales-common-mapper';
import { isOrderInvoiced } from './configuration';

export const updateArticlePriceForKey = (
  articles: INextArticle[],
  key: string = '',
  price: number = 0,
  vatCode: VatCode = null,
  articleType: ArticleType = null
) => {
  let existingArticles = cloneDeep(articles);
  const requestedArticleIndex = existingArticles?.findIndex((article) => article?.key === key);
  if (requestedArticleIndex > -1) {
    existingArticles[requestedArticleIndex].priceExclVat = price;
  } else {
    existingArticles?.push({ key, priceExclVat: price, vatCode, articleType });
  }

  return existingArticles;
};

export const getChargesFromVehicleStatus = (vehicleData: IVehicleStockEcomItem) => {
  const taxes = vehicleData?.vehiclePrice?.taxes || [];
  const updatedWreckage = findChargeTypeValue(taxes, TaxType?.SCRAPDEPOSIT);
  const updatedOneOffTaxWeight = findChargeTypeValue(taxes, TaxType?.TAREWEIGHT);
  const updatedCo2Tax = findChargeTypeValue(taxes, TaxType?.CO2TAX);
  const updatedNoxTax = findChargeTypeValue(taxes, TaxType?.NOXTAX);
  return { updatedWreckage, updatedOneOffTaxWeight, updatedCo2Tax, updatedNoxTax };
};

export const calculateUpdatedOneOffTax = (
  existingArticles: INextArticle[] = [],
  carType: string = null,
  extraWreckage: number = 0,
  oneOffTaxWeight: number = 0,
  co2Tax: number = 0,
  noxTax: number = 0,
  vehicleCondition: VehicleCondition = VehicleCondition.NEW
) => {
  let updatedArticles = cloneDeep(existingArticles);

  //Update articles and get latest oneoffTax
  updatedArticles = updateArticlePriceForKey(
    updatedArticles,
    VehicleConfig.SCRAPDEPOSIT,
    extraWreckage,
    VatCode.NOT_APPLICABLE,
    ArticleType.SCRAPDEPOSIT
  );
  updatedArticles = updateArticlePriceForKey(
    updatedArticles,
    VehicleConfig.TAREWEIGHT,
    oneOffTaxWeight,
    VatCode.NOT_APPLICABLE,
    ArticleType.TAREWEIGHT
  );
  updatedArticles = updateArticlePriceForKey(
    updatedArticles,
    VehicleConfig.CO2TAX,
    co2Tax,
    VatCode.NOT_APPLICABLE,
    ArticleType.CO2TAX
  );
  updatedArticles = updateArticlePriceForKey(
    updatedArticles,
    VehicleConfig.NOXTAX,
    noxTax,
    VatCode.NOT_APPLICABLE,
    ArticleType.NOXTAX
  );

  const updatedVatPrices = calculateVat(updatedArticles, carType, vehicleCondition);
  return {
    oneOffTax: updatedVatPrices?.oneOffTaxAndWreckage,
    totalPrice: updatedVatPrices?.salesTotalIncludingVatAndTax,
  };
};

export const isShowChargeWarning = (order: IOrder = null) => {
  //Charge warning will hide for 1 day after order confirmation if charge value is overwrite by seller.
  if (!order?.vatCalculation?.overriddenVehicleRoadTax && order?.vatCalculation?.overriddenVehicleRoadTax !== 0) {
    return true;
  }

  if (isOffer(order?.status)) {
    return true;
  }

  if (
    !isOrderInvoiced(order) &&
    !isCancelOrder(order?.status) &&
    !isCompletedOrder(null, order) &&
    isConfirmedOrder(order?.events)
  ) {
    const confirmedEvent = order?.events
      ?.filter((event) => {
        const mappedType = EnumMapper.mapEnumStringValueToEnumValue(event, 'eventType', OrderEventType);
        // @ts-expect-error
        return mappedType?.eventType === OrderEventType.CONFIRMED;
      })
      ?.sort((a, b) => new Date(b.doneAt).getTime() - new Date(a.doneAt).getTime())[0];

    if (confirmedEvent) {
      const confirmedDate = new Date(confirmedEvent?.doneAt * 1000);
      const currentDate = new Date();

      const diffMs = currentDate.getTime() - confirmedDate.getTime();

      const is24HoursDone = diffMs >= 24 * 60 * 60 * 1000;
      console.log('is24HoursDone =>', is24HoursDone);
      if (is24HoursDone) {
        return true;
      }
    }
  }

  return false;
};

export const getUpdatedOffTaxForVehicle = (
  order: IOrder,
  configuration: IConfiguration,
  vehicleData: IVehicleStockEcomItem = null,
  existingArticles: INextArticle[] = [],
  isUsedCar: boolean = false,
  isUsedImportCar: boolean = false,
  isForeignBrand: boolean = false
): IVehicleChargeStatus => {
  const isUsedImport = isUsedImportCar ? VehicleCondition.USEDIMPORT : VehicleCondition.USED;
  const vehicleCondition = isUsedCar ? isUsedImport : VehicleCondition.NEW;
  const mappedVehicleCondition = isForeignBrand
    ? mapVehicleConditionForForeignBrand(vehicleCondition)
    : vehicleCondition;
  let status: IVehicleChargeStatus = {
    isTaxChange: false,
    isTaxIncreased: false,
    ExistingOneOffTax: 0,
    UpdatedOneOffTax: 0,
    UpdatedtotalPrice: 0,
  };

  if (isEmpty(order?.vehicles?.[0]?.serialNo) || !configuration?.features?.varebil?.isChargesRecalculationEnabled) {
    return status;
  }

  //Updated charges values from vehicle-status collection
  let existingCharge = 0;
  let updatedCharge = 0;
  if (vehicleData) {
    //execute on view page only
    const {
      updatedWreckage = 0,
      updatedOneOffTaxWeight = 0,
      updatedCo2Tax = 0,
      updatedNoxTax = 0,
    } = getChargesFromVehicleStatus(vehicleData);

    const { oneOffTax, totalPrice } = calculateUpdatedOneOffTax(
      existingArticles,
      order?.pimSnapshot?.vehicleType,
      updatedWreckage,
      updatedOneOffTaxWeight,
      updatedCo2Tax,
      updatedNoxTax,
      mappedVehicleCondition
    );
    existingCharge = order?.chargesAreOverridden
      ? order?.preliminaryOneOffCharges
      : order?.vatCalculation?.oneOffTaxAndWreckage;
    updatedCharge = oneOffTax;
    status.UpdatedtotalPrice = totalPrice;
  } else {
    //execute on all other pages
    updatedCharge =
      order?.vatCalculation?.overriddenVehicleRoadTax !== null
        ? order?.vatCalculation?.overriddenVehicleRoadTax
        : order?.vatCalculation?.oneOffTaxAndWreckage;
    existingCharge = order?.preliminaryOneOffCharges ?? updatedCharge;
    status.UpdatedtotalPrice = order?.vatCalculation?.salesTotalIncludingVatAndTax;
  }

  const isChargeWarningShow = isShowChargeWarning(order);
  status = {
    ...status,
    isTaxChange: isChargeWarningShow ? existingCharge !== updatedCharge : false,
    isTaxIncreased: existingCharge < updatedCharge,
    ExistingOneOffTax: existingCharge || 0,
    UpdatedOneOffTax: updatedCharge || 0,
  };

  console.log('Vehicle charge status =>', JSON.stringify(status));
  return status;
};
